import { match } from "@codesandbox/states";
import { useRestartCommand } from "commands/definitions/useRestartCommand";
import { useEditor } from "features/editor/Feature";
import {
  Stack,
  styled,
  Button,
  Text,
  Loading,
  Tooltip,
  Icon,
} from "prism-react";
import React, { useState } from "react";

import { useAnalytics } from "hooks/useAnalytics";

import { useProjectSettingsContext } from "../SettingsProvider";

import { Form } from "./Form";
import type { Secrets } from "./useEnvironmentVariables";
import { useEnvironmentVariables } from "./useEnvironmentVariables";

const StyledRestartStack = styled(Stack, {
  position: "relative",
  justifyContent: "space-between",
  px: "$3",
  py: "$2",
  background: "$neutral-bg-medium",
  borderRadius: "$2",
});

export const SettingsEnvVariables: React.FC<{
  visible: boolean;
}> = ({ visible }) => {
  const track = useAnalytics();
  const { setVisibility } = useProjectSettingsContext();

  const editor = useEditor();
  const [state, dispatchSecrets] = useEnvironmentVariables();
  const [plainTextMode, setPlainTextMode] = useState(false);

  const [showRestartButton, setShowRestartButton] = React.useState(false);
  const prevState = React.useRef<typeof state | undefined>(undefined);
  const restartContainer = useRestartCommand();

  const handleSubmitNewSecrets = (newSecrets: Secrets) => {
    dispatchSecrets({
      type: "PUT_SECRETS",
      secrets: newSecrets,
    });
  };

  const handleDeleteSecret = (key: string, secrets: Secrets) => {
    dispatchSecrets({
      type: "DELETE_SECRET",
      key,
      secrets,
    });
  };

  React.useEffect(() => {
    if (visible) {
      dispatchSecrets({ type: "GET_SECRETS" });
      track("env_variables");
    }
  }, [visible]);

  React.useEffect(() => {
    if (
      state.state === "IDLE" &&
      (prevState.current?.state === "PUTTING" ||
        prevState.current?.state === "DELETING")
    ) {
      setShowRestartButton(true);
    }

    prevState.current = state;
  }, [state]);

  return (
    <Stack css={{ height: "100%" }} gap={6}>
      <Stack
        css={{
          justifyContent: "space-between",
        }}
        gap={2}
        horizontal
      >
        <Stack>
          <Text>
            The variables will be shared between all branches but not commited
            to your version control.
          </Text>

          {!editor.isPrivate && (
            <Stack
              css={{ color: "$informative-info-base", marginTop: "$2" }}
              horizontal
            >
              <Icon size={4} type="circleBang" />
              <Text color="informative-info-base">
                Make sure to not log any secrets to the terminal or console.{" "}
                <a
                  href="/docs/learn/environment/secrets#privacy"
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  Learn more.
                </a>
              </Text>
            </Stack>
          )}
        </Stack>
        {/**
         * Temporary solution to the edge case where the user types something
         * but doesn't save. The changes get lost when toggling the edit mode.
         */}
        <Tooltip content="Changing edit mode will discard unsaved variables">
          <Button
            css={{
              flexShrink: "0",
            }}
            icon={plainTextMode ? "edit" : "fileHtml"}
            onClick={() => {
              track("toggle_env_variables", {
                mode: !plainTextMode ? "plain" : "form",
              });
              setPlainTextMode(!plainTextMode);
            }}
            variant="ghost"
          >
            {plainTextMode ? "Interface" : "Plain text"}
          </Button>
        </Tooltip>
      </Stack>

      {match(state, {
        IDLE: ({ secrets }) => (
          <Form
            isUpdating={false}
            onDelete={handleDeleteSecret}
            onSubmit={handleSubmitNewSecrets}
            plainTextMode={plainTextMode}
            secrets={secrets}
          />
        ),
        PUTTING: ({ secrets }) => (
          <Form
            onDelete={handleDeleteSecret}
            onSubmit={handleSubmitNewSecrets}
            plainTextMode={plainTextMode}
            secrets={secrets}
            isUpdating
          />
        ),
        GETTING: () => (
          <Stack
            css={{
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Loading size={8} />
          </Stack>
        ),
        DELETING: ({ secrets }) => (
          <Form
            onDelete={handleDeleteSecret}
            onSubmit={handleSubmitNewSecrets}
            plainTextMode={plainTextMode}
            secrets={secrets}
            isUpdating
          />
        ),
        ERROR: ({ message }) => (
          <Stack
            css={{
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text>{message}</Text>
            <Button
              css={{ display: "block", marginTop: "$1" }}
              onClick={() => dispatchSecrets({ type: "GET_SECRETS" })}
              variant="primary"
            >
              Retry
            </Button>
          </Stack>
        ),
      })}

      {showRestartButton && (
        <StyledRestartStack horizontal>
          <Text>
            For the changes to take effect, you need to restart the instance.
          </Text>

          <Button
            onClick={() => {
              restartContainer.perform();
              setVisibility(false);
            }}
            variant="secondary"
          >
            {restartContainer.name}
          </Button>
        </StyledRestartStack>
      )}
    </Stack>
  );
};
